import { useIsFocused } from "@react-navigation/native";
import { useContext, useEffect, useState, useCallback } from "react";
import {
  FlatList,
  RefreshControl,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import Icon from "react-native-vector-icons/MaterialCommunityIcons";
import PageTitle from "../../components/PageTitle";
import TextBoxIcon from "../../components/TextBoxIcon";
import { GlobalContext } from "../../contexts/global";
import { getUserList } from "../../services/Api/admin";
import * as Colors from "../../styles/colors";
import * as Fonts from "../../styles/typography";

const PAGE_SIZE = 20;

export default function AdminUserList({ navigation, route }) {
  const { setLoading, setShowAlert, showAlert } = useContext(GlobalContext);
  const walletMode = route?.params?.walletMode || false;
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const isFocused = useIsFocused();

  useEffect(() => {
    setLoading({
      show: true,
      message: "Carregando usuários",
    });
  }, []);

  useEffect(() => {
    if (isFocused) {
      loadUsers(1, true);
    }
  }, [isFocused]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadUsers(1, true);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const loadUsers = useCallback(
    (pageNum = 1, reset = false) => {
      if (loadingMore && !reset) return;

      if (reset) {
        setLoadingMore(false);
      } else {
        setLoadingMore(true);
      }

      getUserList(search || null, pageNum, PAGE_SIZE)
        .then((response) => {
          const newUsers = response.items || [];
          if (reset) {
            setUsers(newUsers);
          } else {
            setUsers((prev) => [...prev, ...newUsers]);
          }
          setPage(pageNum);
          setHasMore(pageNum < response.totalPages);
        })
        .catch((error) => {
          const message =
            error?.message || error?.erro || "Erro ao carregar usuários";
          setShowAlert({
            type: "alert",
            title: "Erro",
            message: message,
            show: true,
            confirmText: "OK",
            pressConfirm: () => {
              setShowAlert({ ...showAlert, show: false });
            },
          });
        })
        .finally(() => {
          setLoading({ show: false, message: "" });
          setRefreshing(false);
          setLoadingMore(false);
        });
    },
    [search, loadingMore, showAlert]
  );

  const handleRefresh = () => {
    setRefreshing(true);
    loadUsers(1, true);
  };

  const handleLoadMore = () => {
    if (hasMore && !loadingMore) {
      loadUsers(page + 1, false);
    }
  };

  const handleUserPress = (user) => {
    if (walletMode) {
      navigation.navigate("AdminWalletAdjust", {
        userId: user.id,
        userName: user.name,
        currentBalance: user.walletBalance || 0,
      });
    } else {
      navigation.navigate("AdminUserDetail", { userId: user.id });
    }
  };

  const renderUserItem = ({ item }) => (
    <TouchableOpacity
      style={styles.userItem}
      onPress={() => handleUserPress(item)}
      activeOpacity={0.7}
    >
      <View style={styles.userInfo}>
        <View style={styles.userHeader}>
          <Text style={styles.userName} numberOfLines={1}>
            {item.name}
          </Text>
          <View
            style={[
              styles.statusBadge,
              { backgroundColor: item.active ? Colors.SUCCESS : Colors.ALERT },
            ]}
          >
            <Text style={styles.statusText}>
              {item.active ? "Ativo" : "Inativo"}
            </Text>
          </View>
        </View>
        <Text style={styles.userEmail} numberOfLines={1}>
          {item.email}
        </Text>
        <Text style={styles.userPhone}>{item.phone || "Sem telefone"}</Text>
        <View style={styles.walletRow}>
          <Icon name="wallet" size={16} color={Colors.WALLET} />
          <Text style={styles.walletBalance}>
            R$ {(item.walletBalance || 0).toFixed(2)}
          </Text>
        </View>
      </View>
      <Icon name="chevron-right" size={24} color={Colors.GRAY_DARK} />
    </TouchableOpacity>
  );

  const renderEmptyList = () => (
    <View style={styles.emptyContainer}>
      <Icon name="account-search" size={128} color={Colors.BLACK} />
      <Text style={styles.emptyText}>
        {search
          ? "Nenhum usuário encontrado para a busca."
          : "Nenhum usuário cadastrado."}
      </Text>
    </View>
  );

  const renderFooter = () => {
    if (loadingMore) {
      return (
        <View style={styles.footerLoader}>
          <Text style={styles.loadingText}>Carregando mais...</Text>
        </View>
      );
    }
    
    if (hasMore && users.length > 0) {
      return (
        <TouchableOpacity style={styles.loadMoreButton} onPress={handleLoadMore}>
          <Text style={styles.loadMoreText}>Carregar mais</Text>
        </TouchableOpacity>
      );
    }
    
    return null;
  };

  return (
    <PageTitle title={walletMode ? "Selecionar Usuário" : "Gerenciar Usuários"} navigation={navigation}>
      <View style={styles.container}>
        <TextBoxIcon
          iconName="magnify"
          placeholderInput="Buscar por nome, email ou documento"
          value={search}
          onChangeTextInput={setSearch}
          styleBox={styles.searchBox}
        />
        <FlatList
          data={users}
          renderItem={renderUserItem}
          keyExtractor={(item) => item.id.toString()}
          contentContainerStyle={users.length === 0 ? styles.emptyList : null}
          ListEmptyComponent={renderEmptyList}
          ListFooterComponent={renderFooter}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              colors={[Colors.PRIMARY]}
              tintColor={Colors.PRIMARY}
            />
          }
          ItemSeparatorComponent={() => <View style={styles.separator} />}
        />
      </View>
    </PageTitle>
  );
}


const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.WHITE,
  },
  searchBox: {
    marginHorizontal: 8,
    marginTop: 8,
  },
  userItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    backgroundColor: Colors.WHITE,
  },
  userInfo: {
    flex: 1,
  },
  userHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  userName: {
    fontSize: Fonts.FONT_SIZE_16,
    fontWeight: "bold",
    color: Colors.BLACK,
    flex: 1,
    marginRight: 8,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
  },
  statusText: {
    fontSize: Fonts.FONT_SIZE_12,
    color: Colors.WHITE,
    fontWeight: "bold",
  },
  userEmail: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
    marginBottom: 2,
  },
  userPhone: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
    marginBottom: 4,
  },
  walletRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  walletBalance: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.WALLET,
    marginLeft: 4,
    fontWeight: "bold",
  },
  separator: {
    height: 1,
    backgroundColor: Colors.GRAY_LIGHT,
    marginHorizontal: 16,
  },
  emptyContainer: {
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    paddingVertical: 48,
  },
  emptyText: {
    marginTop: 24,
    fontSize: Fonts.FONT_SIZE_16,
    color: Colors.BLACK,
    textAlign: "center",
  },
  emptyList: {
    flexGrow: 1,
  },
  footerLoader: {
    padding: 16,
    alignItems: "center",
  },
  loadingText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
  },
  loadMoreButton: {
    padding: 16,
    alignItems: "center",
    backgroundColor: Colors.SNOW,
    marginHorizontal: 16,
    marginVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.PRIMARY,
  },
  loadMoreText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.PRIMARY,
    fontWeight: "bold",
  },
});
